import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { warningAPI, MonitorRunData } from '../../services/api';

// /warning/runs/:id — 单条监测会话详情 (三级: 列表点进看单条)
const TH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  ok: '#1E8449', warn: '#D4770C', critical: '#C0392B', net: '#C0392B', slope: '#6A4C93',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9', accent: '#C2533A',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
const VERDICT = { safe: ['正常', TH.ok], warn: ['预警', TH.warn], alarm: ['报警', TH.critical] } as const;
const SCEN: Record<string, string> = { normal: '正常', screenout: '砂堵', frac_hit: '压窜' };
const MODE_COLOR: Record<string, string> = { I: '#1E8449', II: '#2874A6', IIIa: '#D4770C', IV: '#C0392B', V: '#6A4C93' };
const MODE_LABEL: Record<string, string> = { I: '正常延伸', II: '高度增长', IIIa: '砂堵前兆', IV: '砂堵', V: '压窜/破坏' };

export default function WarningRunDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState<MonitorRunData | null>(null);
  const [loading, setLoading] = useState(true);
  type Pred = { horizon_s: number; t: number[]; pred: number[]; actual: number[] };
  const [pred, setPred] = useState<Pred | null>(null);
  const [training, setTraining] = useState(false);
  const [predicting, setPredicting] = useState(false);
  // 动态回放
  const [replay, setReplay] = useState(false);
  const [cursor, setCursor] = useState(0);          // 当前回放时刻 (秒)
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(300);          // 回放倍速

  const train = async () => {
    setTraining(true);
    try {
      const { data } = await warningAPI.trainModel();
      message.success(`训练完成: ${data.runs} 段 / ${data.trained_samples} 样本`);
    } catch (e: any) {
      const dd = e?.response?.data;
      message.error(typeof dd === 'string' ? dd : dd?.detail || '训练失败');
    } finally { setTraining(false); }
  };
  const startReplay = async () => {
    if (!id) return;
    setPredicting(true);
    try {
      let p = pred;
      if (!p) {
        const { data } = await warningAPI.predictRun(Number(id));
        p = data; setPred(data);
      }
      if (!p.t.length) { message.info('该段无可预测的泵注段'); return; }
      setReplay(true); setCursor(0); setPlaying(true);
    } catch (e: any) {
      const dd = e?.response?.data;
      message.error(typeof dd === 'string' ? dd : dd?.detail || '预测失败');
    } finally { setPredicting(false); }
  };

  // 回放动画: playing 时按倍速推进 cursor
  useEffect(() => {
    if (!replay || !playing || !d) return;
    const dur = d.run.duration_s || 1;
    const tick = setInterval(() => {
      setCursor((c) => {
        const nx = c + speed * 0.05;               // 每 50ms 推进 speed×0.05 秒
        if (nx >= dur) { setPlaying(false); return dur; }
        return nx;
      });
    }, 50);
    return () => clearInterval(tick);
  }, [replay, playing, speed, d]);

  const load = () => {
    if (!id) return;
    setLoading(true);
    warningAPI.getRunData(Number(id))
      .then(({ data }) => setD(data))
      .catch((e) => message.error(e?.message || '加载失败'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  const ack = async (eid: number) => {
    try { await warningAPI.acknowledge(eid); message.success('已确认'); load(); } catch { message.error('确认失败'); }
  };

  if (loading) return <div style={{ padding: 40, color: TH.textSec }}>加载中…</div>;
  if (!d) return <div style={{ padding: 40, color: TH.textSec }}>会话不存在</div>;
  const [vLabel, vColor] = VERDICT[d.run.verdict] || ['—', TH.textSec];

  // 回放当前数值: 实时实测压力 + 对未来 horizon 秒的预测压力
  const dtRun = d.run.dt_s || 1;
  const curActual = d.samples.length
    ? d.samples[Math.min(d.samples.length - 1, Math.max(0, Math.round(cursor / dtRun)))].surface_pressure_mpa
    : null;
  let curPred: number | null = null;
  if (pred && pred.t.length) {
    const h = pred.horizon_s; let bd = Infinity;
    pred.t.forEach((t, i) => { const dd = Math.abs((t - h) - cursor); if (dd < bd) { bd = dd; curPred = pred.pred[i]; } });
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => nav('/warning/runs')} style={{ border: `1px solid ${TH.headerBorder}`, background: '#fff', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: TH.textSec }}>← 返回列表</button>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>监测会话 #{d.run.id} · 第 {d.run.stage_number} 段</h2>
        <span style={{ padding: '2px 10px', fontSize: 12, fontWeight: 700, borderRadius: 10, background: vColor, color: '#fff' }}>{vLabel}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={train} disabled={training} style={{
            padding: '4px 12px', fontSize: 12, borderRadius: 4, cursor: training ? 'wait' : 'pointer',
            border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.textSec,
          }}>{training ? '训练中…' : '训练泵压模型'}</button>
          <button onClick={startReplay} disabled={predicting} style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: predicting ? 'wait' : 'pointer',
            border: 'none', background: '#6A4C93', color: '#fff',
          }}>{predicting ? '准备中…' : '▶ 动态回放(实测 vs 预测)'}</button>
        </div>
      </div>

      {/* 状态条 */}
      <div style={{ display: 'flex', gap: 1, background: TH.headerBorder }}>
        {[
          ['场景', SCEN[d.run.scenario]], ['峰值压力', `${d.run.peak_pressure_mpa} MPa`],
          ['平均排量', `${d.run.avg_rate_m3min} m³/min`], ['最大砂比', `${d.run.max_conc_kgm3} kg/m³`],
          ['事件数', d.events.length], ['时长', `${Math.round(d.run.duration_s / 60)} min`],
        ].map(([l, v], i) => (
          <div key={i} style={{ flex: 1, padding: '6px 8px', textAlign: 'center', background: '#fff' }}>
            <div style={{ fontSize: 9, color: TH.textMuted }}>{l}</div>
            <div style={{ fontSize: 14, fontWeight: 700, fontFamily: TH.fontMono, color: TH.text }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateRows: '1fr 220px', minHeight: 0 }}>
        <div style={{ borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.accent, background: TH.panelHeaderBg }}>
            压裂施工曲线 · 井压力 / 施工排量 / 砂浓度{pred ? ` · 紫虚线=未来${pred.horizon_s}s泵压预测(红点=实测显著偏离)` : ''}
          </div>
          {replay && (
            <div style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${TH.headerBorder}`, background: '#FAFBFC' }}>
              <button onClick={() => setPlaying((p) => !p)} style={{
                width: 30, height: 26, borderRadius: 4, border: 'none', background: '#6A4C93', color: '#fff', cursor: 'pointer', fontSize: 13,
              }}>{playing ? '⏸' : '▶'}</button>
              <input type="range" min={0} max={d.run.duration_s} value={Math.round(cursor)}
                onChange={(e) => { setPlaying(false); setCursor(Number(e.target.value)); }}
                style={{ flex: 1 }} />
              <span style={{ fontSize: 12, fontFamily: TH.fontMono, color: TH.text, whiteSpace: 'nowrap' }}>
                {Math.floor(cursor / 60)}:{String(Math.floor(cursor % 60)).padStart(2, '0')} / {Math.floor(d.run.duration_s / 60)}:{String(Math.floor(d.run.duration_s % 60)).padStart(2, '0')}
              </span>
              <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))}
                style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: `1px solid ${TH.headerBorder}` }}>
                <option value={60}>60×</option>
                <option value={300}>300×</option>
                <option value={1000}>1000×</option>
              </select>
              <button onClick={() => { setReplay(false); setPlaying(false); }} style={{
                fontSize: 12, padding: '3px 10px', borderRadius: 4, border: `1px solid ${TH.headerBorder}`, background: '#fff', color: TH.textSec, cursor: 'pointer',
              }}>退出回放</button>
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0 }}>
            {replay ? (
              <div style={{ height: '100%', display: 'grid', gridTemplateColumns: '1fr 360px', gap: 1, background: TH.headerBorder }}>
                {/* 左: 整段总览 + 游标 */}
                <div style={{ background: '#fff', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '2px 10px', fontSize: 10, color: TH.textMuted, background: TH.panelHeaderBg }}>整段总览(游标=当前播放位置)</div>
                  <div style={{ flex: 1, minHeight: 0 }}><OverviewChart data={d} cursor={cursor} /></div>
                </div>
                {/* 右: 细节窗口 + 数值 */}
                <div style={{ background: '#fff', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '2px 10px', fontSize: 10, color: TH.textMuted, background: TH.panelHeaderBg }}>细节窗口(红=实测,紫=超前{pred?.horizon_s ?? 60}s预测)</div>
                  <div style={{ flex: 1, minHeight: 0 }}><DetailChart data={d} pred={pred} cursor={cursor} /></div>
                  <div style={{ borderTop: `1px solid ${TH.headerBorder}`, padding: '10px 14px', display: 'flex', gap: 18 }}>
                    <div>
                      <div style={{ fontSize: 11, color: TH.textMuted }}>实时井压力</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: '#C0392B', fontFamily: TH.fontMono }}>
                        {curActual != null ? curActual.toFixed(1) : '—'}<span style={{ fontSize: 12, fontWeight: 400, color: TH.textSec }}> MPa</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: TH.textMuted }}>预测 +{pred?.horizon_s ?? 60}s</div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: '#6A4C93', fontFamily: TH.fontMono }}>
                        {curPred != null ? (curPred as number).toFixed(1) : '—'}<span style={{ fontSize: 12, fontWeight: 400, color: TH.textSec }}> MPa</span>
                      </div>
                    </div>
                    {curActual != null && curPred != null && (
                      <div>
                        <div style={{ fontSize: 11, color: TH.textMuted }}>预测趋势</div>
                        <div style={{ fontSize: 26, fontWeight: 800, fontFamily: TH.fontMono, color: (curPred as number) - curActual > 1 ? '#C0392B' : (curPred as number) - curActual < -1 ? '#1E8449' : TH.textSec }}>
                          {(curPred as number) - curActual > 0.3 ? '↑' : (curPred as number) - curActual < -0.3 ? '↓' : '→'}
                          <span style={{ fontSize: 14 }}> {((curPred as number) - curActual).toFixed(1)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <DetailChart data={d} pred={pred} cursor={undefined} />
            )}
          </div>
        </div>
        <div style={{ overflow: 'auto' }}>
          <div style={{ padding: '4px 12px', fontSize: 11, fontWeight: 600, color: TH.accent, background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>事件 ({d.events.length})</div>
          {d.events.length === 0 ? (
            <div style={{ padding: 20, color: TH.textMuted, fontSize: 13 }}>✓ 全程未触发预警</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ background: TH.panelHeaderBg }}>
                {['时刻', '等级', '模式', '斜率', '描述', '处置建议', '状态'].map((h) => <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {d.events.map((e) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #F0EDE4', opacity: e.acknowledged ? 0.5 : 1 }}>
                    <td style={{ padding: '6px 10px', fontFamily: TH.fontMono }}>{Math.floor(e.t_s / 60)}m{Math.round(e.t_s % 60)}s</td>
                    <td style={{ padding: '6px 10px' }}><span style={{ padding: '2px 8px', fontSize: 11, borderRadius: 10, background: e.level === 'critical' ? TH.critical : TH.warn, color: '#fff' }}>{e.level === 'critical' ? '严重' : '警告'}</span></td>
                    <td style={{ padding: '6px 10px' }}><span style={{ color: MODE_COLOR[e.mode], fontWeight: 700 }}>{e.mode}</span> <span style={{ color: TH.textMuted, fontSize: 11 }}>{MODE_LABEL[e.mode]}</span></td>
                    <td style={{ padding: '6px 10px', fontFamily: TH.fontMono }}>{e.slope.toFixed(2)}</td>
                    <td style={{ padding: '6px 10px', maxWidth: 300 }}>{e.message}</td>
                    <td style={{ padding: '6px 10px', color: TH.textSec, maxWidth: 240, fontSize: 11 }}>{e.suggestion}</td>
                    <td style={{ padding: '6px 10px' }}>{e.acknowledged ? <span style={{ color: TH.ok, fontSize: 11 }}>✓已确认</span> : <button onClick={() => ack(e.id)} style={{ padding: '3px 10px', fontSize: 11, borderRadius: 3, background: '#2874A6', color: '#fff', border: 'none', cursor: 'pointer' }}>确认</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// 标准压裂施工曲线: 井压力(红,左轴 MPa) + 施工排量(蓝,右轴 m³/min) + 砂浓度(橙填充)
// + 可选泵压预测虚线(紫) + 残差报警点(红)
function DetailChart({ data, pred, cursor }: { data: MonitorRunData; pred?: { horizon_s: number; t: number[]; pred: number[]; actual: number[] } | null; cursor?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = TH.canvasBg; ctx.fillRect(0, 0, W, H);
    const s = data.samples; if (!s.length) return;
    const PRES = '#C0392B', RATE = '#2874A6', CONC = '#D4770C';
    const padL = 52, padR = 56, padT = 24, padB = 30;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const tMax = s[s.length - 1].t_s || 1;
    const horizon = pred?.horizon_s || 60;
    const WIN = 300;                                          // 回放滚动窗口宽 (秒, 看清 60s 超前)
    const xMin = cursor == null ? 0 : Math.max(0, cursor - WIN);
    const xMax = cursor == null ? tMax : Math.min(tMax + horizon, cursor + horizon);
    const pMax = Math.max(10, Math.ceil(Math.max(...s.map((x) => x.surface_pressure_mpa || 0)) / 10) * 10);
    const rMax = Math.max(1, Math.max(...s.map((x) => x.rate_m3min || 0)) * 1.15);
    const cPeak = Math.max(...s.map((x) => x.proppant_conc_kgm3 || 0));
    const cMax = Math.max(1, cPeak * 1.15);
    const X = (t: number) => padL + ((t - xMin) / (xMax - xMin || 1)) * plotW;
    const Yp = (v: number) => padT + (1 - v / pMax) * plotH;                 // 压力 左轴
    const Yr = (v: number) => padT + (1 - v / rMax) * plotH;                 // 排量 右轴
    const Yc = (v: number) => padT + plotH * (1 - 0.55 * v / cMax);          // 砂浓度 占底部 55%

    // 横向网格 + 左(压力)右(排量)刻度
    ctx.font = `9px ${TH.fontMono}`;
    for (let k = 0; k <= 4; k++) {
      const y = padT + (k / 4) * plotH;
      ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = PRES; ctx.textAlign = 'right'; ctx.fillText((pMax * (1 - k / 4)).toFixed(0), padL - 5, y + 3);
      ctx.fillStyle = RATE; ctx.textAlign = 'left'; ctx.fillText((rMax * (1 - k / 4)).toFixed(0), W - padR + 5, y + 3);
    }
    // X 轴时间(分钟) — 适配当前窗口
    const spanMin = (xMax - xMin) / 60;
    const step = spanMin > 120 ? 30 : spanMin > 40 ? 10 : spanMin > 12 ? 5 : 2;
    ctx.textAlign = 'center';
    for (let m = Math.ceil(xMin / 60 / step) * step; m * 60 <= xMax; m += step) {
      const x = X(m * 60);
      ctx.strokeStyle = TH.grid; ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke();
      ctx.fillStyle = TH.textMuted; ctx.fillText(`${m}`, x, H - padB + 14);
    }

    // 回放可见数据: cursor 定义时只显示窗口内 [xMin, cursor] 的已发生数据
    const cur = cursor;
    const vis = cur == null ? s : s.filter((p) => p.t_s >= xMin && p.t_s <= cur);
    const visEnd = vis.length ? vis[vis.length - 1].t_s : 0;

    // 砂浓度 底部填充(浅橙)
    if (vis.length) {
      ctx.fillStyle = CONC + '33'; ctx.beginPath(); ctx.moveTo(X(vis[0].t_s), Yc(0));
      vis.forEach((p) => ctx.lineTo(X(p.t_s), Yc(p.proppant_conc_kgm3 || 0)));
      ctx.lineTo(X(visEnd), Yc(0)); ctx.closePath(); ctx.fill();
    }
    // 排量 蓝线
    ctx.strokeStyle = RATE; ctx.lineWidth = 1.3; ctx.beginPath();
    vis.forEach((p, i) => { const x = X(p.t_s), y = Yr(p.rate_m3min || 0); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.stroke();
    // 井压力 红线(实测)
    ctx.strokeStyle = PRES; ctx.lineWidth = 1.5; ctx.beginPath();
    vis.forEach((p, i) => { const x = X(p.t_s), y = Yp(p.surface_pressure_mpa || 0); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.stroke();

    // 泵压预测(紫虚线): 显示"已做出"的预测 = 源时刻(t-horizon)<=cursor,
    // 其目标时刻 t 可超前到 cursor+horizon → 紫线领先实测红线 60s
    if (pred && pred.t.length) {
      const lim = cur == null ? Infinity : cur;
      ctx.strokeStyle = '#6A4C93'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 3]); ctx.beginPath();
      let started = false;
      pred.t.forEach((t, i) => {
        if (t - horizon > lim || t < xMin) return;          // 源时刻>cursor 还没预测; 窗口外不画
        const x = X(t), y = Yp(pred.pred[i]);
        if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
      });
      ctx.stroke(); ctx.setLineDash([]);
      // 残差报警点: 只标已发生(目标<=cursor)且偏离大的
      const thr = Math.max(3, pMax * 0.06);
      ctx.fillStyle = '#C0392B';
      pred.t.forEach((t, i) => {
        if (t > lim || t < xMin) return;
        if (Math.abs((pred.actual[i] ?? 0) - pred.pred[i]) > thr) {
          ctx.beginPath(); ctx.arc(X(t), Yp(pred.actual[i] ?? 0), 2.4, 0, Math.PI * 2); ctx.fill();
        }
      });
    }

    // 回放当前时刻竖线 + 当前压力点
    if (cur != null) {
      ctx.strokeStyle = '#6A4C93'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(X(cur), padT); ctx.lineTo(X(cur), H - padB); ctx.stroke(); ctx.setLineDash([]);
      if (vis.length) {
        ctx.fillStyle = PRES; ctx.beginPath();
        ctx.arc(X(visEnd), Yp(vis[vis.length - 1].surface_pressure_mpa || 0), 3, 0, Math.PI * 2); ctx.fill();
      }
    }

    // 事件竖线(若有)
    data.events.forEach((e) => {
      const x = X(e.t_s); ctx.strokeStyle = e.level === 'critical' ? TH.critical : TH.warn; ctx.lineWidth = 1.2; ctx.setLineDash([2, 2]);
      ctx.beginPath(); ctx.moveTo(x, padT); ctx.lineTo(x, H - padB); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = e.level === 'critical' ? TH.critical : TH.warn; ctx.font = `bold 9px ${TH.fontUI}`; ctx.textAlign = 'left'; ctx.fillText(e.mode, x + 2, padT + 9);
    });

    // 图例
    ctx.font = `10px ${TH.fontUI}`; ctx.textAlign = 'left';
    const leg: [string, string][] = [
      ['井压力 MPa', PRES], ['排量 m³/min', RATE], [`砂浓度 kg/m³ (峰 ${cPeak.toFixed(0)})`, CONC],
    ];
    let lx = padL + 2;
    leg.forEach(([t, c]) => {
      ctx.fillStyle = c; ctx.fillRect(lx, padT - 13, 12, 3);
      ctx.fillStyle = TH.textSec; ctx.fillText(t, lx + 15, padT - 9);
      lx += 18 + ctx.measureText(t).width + 18;
    });
    ctx.fillStyle = TH.textMuted; ctx.textAlign = 'right'; ctx.fillText('时间 (min)', W - padR, H - 5);
  }, [data, pred, cursor]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

// 整段总览: 全段压力(淡)+ 已播放(实)+ 砂比填充 + 游标竖线 + 细节窗口框
function OverviewChart({ data, cursor }: { data: MonitorRunData; cursor: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
    const s = data.samples; if (!s.length) return;
    const PRES = '#C0392B', CONC = '#D4770C';
    const padL = 44, padR = 14, padT = 16, padB = 26;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const tMax = s[s.length - 1].t_s || 1;
    const pMax = Math.max(10, Math.ceil(Math.max(...s.map((x) => x.surface_pressure_mpa || 0)) / 10) * 10);
    const cMax = Math.max(1, Math.max(...s.map((x) => x.proppant_conc_kgm3 || 0)) * 1.15);
    const X = (t: number) => padL + (t / tMax) * plotW;
    const Yp = (v: number) => padT + (1 - v / pMax) * plotH;
    const Yc = (v: number) => padT + plotH * (1 - 0.5 * v / cMax);
    ctx.font = `9px ${TH.fontMono}`;
    for (let k = 0; k <= 3; k++) {
      const y = padT + (k / 3) * plotH;
      ctx.strokeStyle = TH.grid; ctx.lineWidth = 0.5; ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.fillStyle = PRES; ctx.textAlign = 'right'; ctx.fillText((pMax * (1 - k / 3)).toFixed(0), padL - 4, y + 3);
    }
    const totMin = tMax / 60; const step = totMin > 180 ? 60 : totMin > 60 ? 30 : 10;
    ctx.textAlign = 'center'; ctx.fillStyle = TH.textMuted;
    for (let m = 0; m <= totMin; m += step) ctx.fillText(`${m}`, X(m * 60), H - padB + 13);
    // 砂比填充(淡)
    ctx.fillStyle = CONC + '22'; ctx.beginPath(); ctx.moveTo(X(0), Yc(0));
    s.forEach((p) => ctx.lineTo(X(p.t_s), Yc(p.proppant_conc_kgm3 || 0)));
    ctx.lineTo(X(tMax), Yc(0)); ctx.closePath(); ctx.fill();
    // 全段压力(淡)
    ctx.strokeStyle = PRES + '55'; ctx.lineWidth = 1; ctx.beginPath();
    s.forEach((p, i) => { const x = X(p.t_s), y = Yp(p.surface_pressure_mpa || 0); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.stroke();
    // 已播放压力(实)
    ctx.strokeStyle = PRES; ctx.lineWidth = 1.4; ctx.beginPath(); let st = false;
    s.forEach((p) => { if (p.t_s > cursor) return; const x = X(p.t_s), y = Yp(p.surface_pressure_mpa || 0); if (!st) { ctx.moveTo(x, y); st = true; } else ctx.lineTo(x, y); });
    ctx.stroke();
    // 细节窗口框 + 游标竖线
    const wx0 = X(Math.max(0, cursor - 300)), wx1 = X(Math.min(tMax, cursor + 60));
    ctx.strokeStyle = 'rgba(106,76,147,0.35)'; ctx.lineWidth = 1; ctx.strokeRect(wx0, padT, Math.max(2, wx1 - wx0), plotH);
    ctx.strokeStyle = '#6A4C93'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(X(cursor), padT); ctx.lineTo(X(cursor), H - padB); ctx.stroke();
    ctx.fillStyle = TH.textMuted; ctx.textAlign = 'left'; ctx.font = `9px ${TH.fontUI}`; ctx.fillText('压力 MPa', padL, padT - 4);
    ctx.textAlign = 'right'; ctx.fillText('时间 min', W - padR, H - 4);
  }, [data, cursor]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
